import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import ProfileHeader from "./ProfileHeader";
import TabNavigation from "./TabNavigation";
import OverviewView from "./views/OverviewView";
import FinalRemarksView from "./views/FinalRemarksView";
import ScreeningQuestionsView from "./views/ScreeningQuestionsView";
import ProctoringLogsView from "./views/ProctoringLogsView";
import ScreenshotsView from "./views/ScreenshotsView";
import { RatingSystem } from "./views/RatingSystemView";
import { useSelector } from "react-redux";
import {
  getFeedbackForm,
  getProfileForm,
  getShowFinalRemarks,
} from "../../../redux/reducer/incbotFeedbackReducer";
import { AnimatePresence, motion } from "framer-motion";

const Index = () => {
  const profileForm = useSelector(getProfileForm);
  const feedbackForm = useSelector(getFeedbackForm);
  const showFinalRemarks = useSelector(getShowFinalRemarks);

  const [activeTab, setActiveTab] = useState("overview");
  const prevTabIndex = useRef(0);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "final-remarks", label: "Final Remarks" },
    { id: "screening-questions", label: "Screening Questions" },
  ];

  if (feedbackForm?.proctoringLogs?.status)
    tabs.push({ id: "proctoring-logs", label: "Proctoring Logs" });
  if (feedbackForm?.screenshots?.status || feedbackForm?.deviceScreenshots?.status)
    tabs.push({ id: "screenshots", label: "Screenshots" });
  tabs.push({ id: "rating-system", label: "Rating System" });

  const currentTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const direction = currentTabIndex > prevTabIndex.current ? 1 : -1;

  const renderActiveView = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewView key="overview" />;
      case "final-remarks":
        return <FinalRemarksView key="final-remarks" />;
      case "screening-questions":
        return <ScreeningQuestionsView key="screening-questions" />;
      case "proctoring-logs":
        return <ProctoringLogsView key="proctoring-logs" />;
      case "screenshots":
        return <ScreenshotsView key="screenshots" />;
      case "rating-system":
        return <RatingSystem key="rating-system" />;
      default:
        return <OverviewView key="overview" />;
    }
  };

  useEffect(() => {
    if (profileForm?.name) {
      document.title = `${profileForm.name} CEFR Feedback Report`;
    }
  }, [profileForm]);

  const handleTabChange = (id: string) => {
    prevTabIndex.current = tabs.findIndex((tab) => tab.id === activeTab);
    setActiveTab(id);
  };

  if (!profileForm || !feedbackForm) return null;

  return (
    <div className="min-h-screen min-w-[320px] flex w-full justify-center">
      {profileForm?.name && (
        <Helmet>
          <title>{profileForm.name} CEFR Feedback Report | InCruiter</title>
          <meta
            name="description"
            content={`CEFR interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
          <meta
            property="og:title"
            content={`${profileForm.name} CEFR Feedback Report | InCruiter`}
          />
          <meta
            property="og:description"
            content={`CEFR interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
          <meta
            name="twitter:title"
            content={`${profileForm.name} CEFR Feedback Report | InCruiter`}
          />
          <meta
            name="twitter:description"
            content={`CEFR interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
          />
        </Helmet>
      )}
      <div className="max-w-6xl min-w-[320px] w-full bg-gray-50 print-a3-container">
        <ProfileHeader />
        <div className="hidden md:block print:hidden">
          <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
          <div className="bg-gray-50 overflow-hidden relative">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeTab}
                custom={direction}
                initial={{ x: direction * 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: direction * -100, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {renderActiveView()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div id="print-section" className="md:hidden print:block space-y-8 print:md:block">
          <div className="bg-gray-50">
            <OverviewView key="overview" />
            {showFinalRemarks && (
              <>
                <FinalRemarksView key="final-remarks" />
                <div className="page-break"></div>
              </>
            )}
            <ScreeningQuestionsView key="screening-questions" isPrint />
            <div className="page-break"></div>
            {feedbackForm?.proctoringLogs?.status && (
              <>
                <ProctoringLogsView key="proctoring-logs" />
                <div className="page-break"></div>
              </>
            )}
            {(feedbackForm?.screenshots?.status || feedbackForm?.deviceScreenshots?.status) && (
              <>
                <ScreenshotsView key="screenshots" />
                <div className="page-break"></div>
              </>
            )}
            <RatingSystem key="rating-system" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
